import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Evidence } from 'src/app/models/api/finding.model';
import { formatEvidenceLabel, parseAdditionalData } from 'src/app/common/stringUtils';
import { formatFromDateWithSeconds } from 'src/app/common/datetime_utility';
import moment from 'moment';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { LucideIconsModule } from 'src/app/shared/icons/lucide-icons.module';

const EMAIL_FROM_PATTERN = /(?:from["\s]*[:=]["\s]*)([a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+)\.([a-zA-Z]{2,}))/i;

const PLATFORM_SERVICE_PATTERN = /^\.([a-zA-Z0-9]+)-(.+)$/;

export interface EvidenceTableRow {
  connector: string;
  tool: string;
  label: string;
  timestamp: string;
  index: number;
}

@Component({
  selector: 'app-taint-analysis-evidence',
  standalone: true,
  imports: [CommonModule, MatTableModule, LucideIconsModule, LabelsProviderPipe],
  templateUrl: './taint-analysis-evidence.component.html',
  styleUrl: './taint-analysis-evidence.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaintAnalysisEvidenceComponent {
  public evidence = input.required<Evidence[]>();

  private readonly _logger = inject(LoggerService);

  public evidenceTableData: Signal<EvidenceTableRow[]> = computed(() => {
    const evidence = this.evidence();
    if (!evidence?.length) {
      return [];
    }
    return this.transformEvidenceToTableData(evidence);
  });

  public evidenceDataSource: Signal<MatTableDataSource<EvidenceTableRow>> = computed(() => {
    return new MatTableDataSource(this.evidenceTableData());
  });

  public evidenceFlowData: Signal<{ label: string; key: string }[]> = computed(() => {
    const evidence = this.evidence();
    if (!evidence?.length) {
      return [];
    }
    return this.deduplicateEvidence(evidence, (item) => ({
      label: formatEvidenceLabel(item.title),
      key: `${item.coreEvidence ?? ''}|${item.title ?? ''}`,
    }));
  });

  public displayedColumns: string[] = ['connector', 'tool', 'label', 'timestamp'];

  private deduplicateEvidence<T>(items: Evidence[], mapper: (item: Evidence, index: number) => T): T[] {
    const seenKeys = new Set<string>();
    const results: T[] = [];

    items.forEach((item, index) => {
      const key = `${item.coreEvidence ?? ''}|${item.title ?? ''}`;
      if (seenKeys.has(key)) return;
      seenKeys.add(key);
      results.push(mapper(item, index));
    });

    return results;
  }

  private transformEvidenceToTableData(taintAnalysisEvidence: Evidence[]): EvidenceTableRow[] {
    return this.deduplicateEvidence(taintAnalysisEvidence, (item, index) => {
      try {
        const { connector, tool } = this.parseConnectorAndTool(item.coreEvidence ?? '');
        const parsed = parseAdditionalData(item.additionalData);
        if (!parsed) {
          this._logger.warn('Failed to parse taint evidence additionalData', { index, title: item.title });
        }
        const ts = parsed?.['timestamp'];
        return {
          connector,
          tool,
          label: formatEvidenceLabel(item.title),
          timestamp: this.formatTimestamp(typeof ts === 'string' ? ts : undefined),
          index,
        };
      } catch (error) {
        this._logger.error('Error transforming evidence item', { item, index }, error as Error);
        return {
          connector: item.coreEvidence ?? '-',
          tool: '-',
          label: item.title ?? '-',
          timestamp: '-',
          index,
        };
      }
    });
  }

  private formatTimestamp(value: string | undefined): string {
    if (!value) return '-';
    const parsed = moment(value, moment.ISO_8601, true);
    return parsed.isValid() ? formatFromDateWithSeconds(parsed.toDate()) : '-';
  }

  private parseConnectorAndTool(match: string): { connector: string; tool: string } {
    try {
      const emailMatch = match.match(EMAIL_FROM_PATTERN);

      if (emailMatch) {
        const domainName = emailMatch[2];
        const capitalizedDomain = domainName.charAt(0).toUpperCase() + domainName.slice(1).toLowerCase();
        return {
          connector: capitalizedDomain,
          tool: '-',
        };
      }

      const platformMatch = match.match(PLATFORM_SERVICE_PATTERN);
      if (platformMatch) {
        return {
          connector: platformMatch[1],
          tool: platformMatch[2],
        };
      }

      return {
        connector: match,
        tool: '-',
      };
    } catch (error) {
      this._logger.warn('Error parsing connector and tool from match text', { match, error });
      return {
        connector: match,
        tool: '-',
      };
    }
  }
}
